import { useEffect, useEffectEvent, useRef, useState } from "react";
import { Alert, Checkbox, Form, Input, Modal, Radio, Select, Button, Skeleton, Spin, Typography, Upload, message, theme } from "antd";
import { CloseOutlined, PictureOutlined } from "@ant-design/icons";
import {
  AppFeedbackCategory,
  type AppFeedbackAttachmentContentType,
  AppFeedbackRouting,
  type AppFeedbackAttachmentDto,
} from "breuer-supercharged-sdk";
import { useSubmitAppFeedback } from "@/hooks/useSubmitAppFeedback";
import { useFeedbackRoutingOptions } from "@/hooks/useAppFeedbackReview";
import { getConsoleLogBuffer } from "@/utils/console_log_buffer";
import { CapturedScreenshot } from "@/utils/capture_screenshot";
import { prepareFeedbackImage } from "./prepare_feedback_image";
import {
  FEEDBACK_ATTACHMENTS_MAX_TOTAL_BYTES,
  FEEDBACK_MAX_ATTACHMENTS,
  attachmentFileName,
  base64DecodedLength,
  dataUrlBase64,
  fitsAttachmentBudget,
  formatFileSize,
  isSupportedImageType,
  pickClipboardImageFiles,
  planAttachmentIntake,
  shouldAttachPastedImages,
} from "./feedback_attachment_utils";
import {
  canSubmitFeedback,
  describeFeedbackContext,
  feedbackRoutingGate,
  feedbackSubmitErrorMessage,
  feedbackThanksMessage,
  parseFeedbackContext,
  resolveFeedbackRouting,
} from "./feedback_routing";

const { TextArea } = Input;

const FEEDBACK_FORM_ID = "feedback-dialog-form";

const CATEGORY_OPTIONS = [
  { value: AppFeedbackCategory.Bug, label: "Fehler melden" },
  { value: AppFeedbackCategory.Question, label: "Frage stellen" },
  { value: AppFeedbackCategory.Idea, label: "Idee einreichen" },
];

interface FeedbackFormValues {
  category: AppFeedbackCategory;
  message: string;
  includeScreenshot: boolean;
}

/** An image the user attached themselves, prepared for sending. */
interface AttachedImage {
  id: number;
  fileName: string;
  contentType: AppFeedbackAttachmentContentType;
  /** Data URL of the prepared image — thumbnail and (after the comma) payload. */
  dataUrl: string;
  /** Decoded size in bytes. */
  byteLength: number;
}

interface FeedbackDialogProps {
  open: boolean;
  onClose: () => void;
  /** Screenshot loading in the background; null while capturing or if it failed. */
  screenshot: CapturedScreenshot | null;
  /** True while the background capture is still running. */
  capturing: boolean;
}

function isTextField(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLElement &&
    (target.tagName === "TEXTAREA" || target.tagName === "INPUT" || target.isContentEditable)
  );
}

/**
 * Global feedback dialog (opened via the floating feedback button in the app
 * shell). Alongside category + message it automatically attaches the page
 * screenshot, display/viewport size, user agent and the recent browser
 * console log, so reports arrive with debugging context.
 *
 * Users can additionally attach up to 5 own images — pasted with Strg+V
 * anywhere in the open dialog, dropped onto or picked via the upload area.
 * Large ones are downscaled to JPEG (≤ 2560 px) in the browser before sending;
 * small PNG screenshots go out unchanged.
 */
export default function FeedbackDialog({ open, onClose, screenshot, capturing }: FeedbackDialogProps) {
  const [form] = Form.useForm<FeedbackFormValues>();
  const submitFeedback = useSubmitAppFeedback();
  const { token } = theme.useToken();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // „Senden an“: nur bei aktiver Vorprüfung; `null` = Voreinstellung (siehe resolveFeedbackRouting).
  const { data: routingOptions, isFetching: routingFetching, refetch: refetchRouting } = useFeedbackRoutingOptions();
  const [routingChoice, setRoutingChoice] = useState<AppFeedbackRouting | null>(null);
  const routingGate = feedbackRoutingGate(routingOptions, routingFetching);
  const routingReady = canSubmitFeedback(routingGate);
  const effectiveRouting = resolveFeedbackRouting(routingOptions, routingChoice);
  const pageContext = open ? parseFeedbackContext(window.location.pathname) : {};

  // Own images. The ref mirrors the state for the async add flow (several
  // pastes/drops can be processing at once); only event handlers touch it.
  const [images, setImages] = useState<AttachedImage[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const imagesRef = useRef<AttachedImage[]>([]);
  const pendingRef = useRef(0);
  const nextImageId = useRef(1);
  // Bumped on reset, so images still being processed for a closed dialog
  // don't land in the next one.
  const imageSession = useRef(0);

  const commitImages = (next: AttachedImage[]) => {
    imagesRef.current = next;
    setImages(next);
  };

  const changePending = (delta: number) => {
    pendingRef.current = Math.max(0, pendingRef.current + delta);
    setPendingCount(pendingRef.current);
  };

  const resetImages = () => {
    imageSession.current += 1;
    pendingRef.current = 0;
    setPendingCount(0);
    commitImages([]);
  };

  const addImages = async (files: File[]) => {
    const supported = files.filter((f) => isSupportedImageType(f.type));
    if (supported.length < files.length) {
      message.warning("Es können nur Bilder angehängt werden (z. B. PNG oder JPG).");
    }
    const { accepted, rejected } = planAttachmentIntake(
      imagesRef.current.length + pendingRef.current,
      supported.length,
    );
    if (rejected > 0) {
      message.warning(
        `Es können höchstens ${FEEDBACK_MAX_ATTACHMENTS} Bilder angehängt werden` +
          (accepted > 0 ? ` – ${rejected} ${rejected === 1 ? "Bild wurde" : "Bilder wurden"} nicht übernommen.` : "."),
      );
    }
    const batch = supported.slice(0, accepted);
    if (!batch.length) return;

    const session = imageSession.current;
    changePending(batch.length);
    for (const file of batch) {
      const label = file.name ? `„${file.name}“` : "Das Bild";
      try {
        const prepared = await prepareFeedbackImage(file);
        if (imageSession.current !== session) return;
        const byteLength = base64DecodedLength(dataUrlBase64(prepared.dataUrl));
        if (!fitsAttachmentBudget(imagesRef.current.map((i) => i.byteLength), byteLength)) {
          message.warning(
            `${label} wurde nicht angehängt: Alle Bilder zusammen dürfen höchstens ` +
              `${formatFileSize(FEEDBACK_ATTACHMENTS_MAX_TOTAL_BYTES)} groß sein.`,
          );
        } else {
          commitImages([
            ...imagesRef.current,
            {
              id: nextImageId.current++,
              fileName: attachmentFileName(file.name, prepared.contentType),
              contentType: prepared.contentType,
              dataUrl: prepared.dataUrl,
              byteLength,
            },
          ]);
        }
      } catch {
        if (imageSession.current !== session) return;
        message.error(`${label} konnte nicht gelesen werden. Bitte als PNG oder JPG anhängen.`);
      } finally {
        if (imageSession.current === session) changePending(-1);
      }
    }
  };

  const removeImage = (id: number) => {
    commitImages(imagesRef.current.filter((img) => img.id !== id));
  };

  // Strg+V anywhere while the dialog is open. A document listener (instead
  // of onPaste on the content) also catches pastes while focus sits on the
  // modal frame itself, e.g. right after opening.
  const handlePaste = useEffectEvent((event: ClipboardEvent) => {
    const data = event.clipboardData;
    if (!data || isSubmitting) return;
    const files = pickClipboardImageFiles<File>({ files: data.files, items: data.items });
    const clipboardHasText = data.getData("text/plain").trim() !== "";
    if (
      !shouldAttachPastedImages({
        imageCount: files.length,
        clipboardHasText,
        targetIsTextField: isTextField(event.target),
      })
    ) {
      return;
    }
    event.preventDefault();
    void addImages(files);
  });
  useEffect(() => {
    if (!open) return;
    const onPaste = (event: ClipboardEvent) => handlePaste(event);
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  }, [open]);

  const handleSubmit = async (values: FeedbackFormValues) => {
    if (!routingReady) return;
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      const includeScreenshot = values.includeScreenshot ?? true;
      const attachments: AppFeedbackAttachmentDto[] = images.map((img) => ({
        contentType: img.contentType,
        dataBase64: dataUrlBase64(img.dataUrl),
        fileName: img.fileName,
      }));
      const context = parseFeedbackContext(window.location.pathname);
      const response = await submitFeedback.mutateAsync({
        category: values.category,
        message: values.message,
        pageUrl: window.location.href,
        userAgent: navigator.userAgent,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
        devicePixelRatio: window.devicePixelRatio,
        browserLogs: getConsoleLogBuffer(),
        ...(screenshot && includeScreenshot
          ? {
              screenshotBase64: screenshot.base64,
              screenshotContentType: screenshot.contentType,
            }
          : {}),
        ...(attachments.length ? { attachments } : {}),
        ...(effectiveRouting ? { routing: effectiveRouting } : {}),
        ...(Object.keys(context).length ? { context } : {}),
      });
      message.success(feedbackThanksMessage(response));
      resetForm();
      onClose();
    } catch (error) {
      setSubmitError(feedbackSubmitErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    form.resetFields();
    resetImages();
    setRoutingChoice(null);
    setSubmitError(null);
  };

  const handleCancel = () => {
    if (isSubmitting) return;
    resetForm();
    onClose();
  };

  const totalImageBytes = images.reduce((sum, img) => sum + img.byteLength, 0);
  const imageSlotsFull = images.length + pendingCount >= FEEDBACK_MAX_ATTACHMENTS;

  return (
    <Modal
      title="Feedback geben"
      open={open}
      onCancel={handleCancel}
      centered
      footer={
        <div style={{ textAlign: "left" }}>
          {routingGate === "loading" && (
            <div style={{ marginBottom: 16 }} data-testid="feedback-routing-loading" aria-busy="true">
              <Typography.Text strong>Senden an</Typography.Text>
              <Skeleton active title={false} paragraph={{ rows: 2, width: ["60%", "40%"] }} style={{ marginTop: 6 }} />
            </div>
          )}
          {routingGate === "error" && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              data-testid="feedback-routing-error"
              title="Empfänger konnten nicht geladen werden."
              description="Sie können trotzdem senden – das Feedback geht an die für Ihr Unternehmen eingestellten Empfänger."
              action={
                <Button size="small" onClick={() => void refetchRouting()}>
                  Erneut versuchen
                </Button>
              }
            />
          )}
          {routingGate === "choice" && routingOptions && (
            <div style={{ marginBottom: 16 }} data-testid="feedback-routing">
              <Typography.Text strong>Senden an</Typography.Text>
              <Radio.Group
                value={effectiveRouting}
                onChange={(e) => setRoutingChoice(e.target.value)}
                disabled={isSubmitting}
                style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 6 }}
              >
                <Radio value={AppFeedbackRouting.Review}>
                  An Breuer-Verantwortliche (Vorprüfung)
                  <Typography.Text type="secondary" style={{ display: "block", fontSize: 12 }}>
                    geht an: {routingOptions.reviewers.map((r) => r.displayName).join(", ")}
                  </Typography.Text>
                </Radio>
                <Radio value={AppFeedbackRouting.Developer}>Direkt an Entwickler</Radio>
              </Radio.Group>
              <Typography.Text type="secondary" style={{ display: "block", fontSize: 12, marginTop: 6 }}>
                Bei dringenden Störungen bitte direkt an Entwickler.
              </Typography.Text>
            </div>
          )}

          {submitError && (
            <Alert
              type="error"
              showIcon
              role="alert"
              closable={{ onClose: () => setSubmitError(null) }}
              style={{ marginBottom: 16 }}
              data-testid="feedback-submit-error"
              title={submitError}
            />
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={handleCancel}>Abbrechen</Button>
            <Button
              type="primary"
              htmlType="submit"
              form={FEEDBACK_FORM_ID}
              loading={isSubmitting}
              disabled={pendingCount > 0 || !routingReady}
            >
              Feedback senden
            </Button>
          </div>
        </div>
      }
      width={600}
      destroyOnHidden
      styles={{ body: { maxHeight: "calc(100vh - 340px)", minHeight: 160, overflowY: "auto", paddingInline: 2 } }}
    >
      <Form
        id={FEEDBACK_FORM_ID}
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        disabled={isSubmitting}
        initialValues={{ includeScreenshot: true }}
      >
        <Form.Item
          label="Kategorie"
          name="category"
          rules={[{ required: true, message: "Bitte wählen Sie eine Kategorie." }]}
        >
          <Select placeholder="Kategorie wählen" options={CATEGORY_OPTIONS} />
        </Form.Item>

        <Form.Item
          label="Nachricht"
          name="message"
          rules={[
            { required: true, message: "Bitte beschreiben Sie Ihr Anliegen." },
            { min: 3, message: "Bitte mindestens 3 Zeichen eingeben." },
          ]}
        >
          <TextArea
            rows={5}
            showCount
            maxLength={5000}
            placeholder="Beschreiben Sie Ihren Fehler, Ihre Frage oder Ihre Idee möglichst konkret …"
          />
        </Form.Item>

        {/* Fixed-height preview area: reserved from the moment the dialog
            opens, so the arriving screenshot never shifts the layout. */}
        <div
          style={{
            height: 180,
            marginBottom: 8,
            border: `1px solid ${token.colorBorder}`,
            borderRadius: 6,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          {capturing ? (
            <Spin size="small" tip="Screenshot wird erstellt …">
              {/* Spin needs a child to render the tip below the spinner. */}
              <div style={{ width: 120, height: 40 }} />
            </Spin>
          ) : screenshot ? (
            <img
              src={screenshot.dataUrl}
              alt="Screenshot der aktuellen Seite"
              style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
            />
          ) : (
            <Typography.Text type="secondary">
              Screenshot konnte nicht erstellt werden
            </Typography.Text>
          )}
        </div>
        <Form.Item name="includeScreenshot" valuePropName="checked" style={{ marginBottom: 12 }}>
          <Checkbox disabled={!screenshot}>Screenshot der aktuellen Seite mitsenden</Checkbox>
        </Form.Item>

        {/* Own images (App-Feedback 2026-08-04: „habe einen Screenshot
            gemacht, weiß aber nicht, wie ich ihn hier einfügen kann“). */}
        <div style={{ marginBottom: 12 }} data-testid="feedback-attachments">
          <div style={{ marginBottom: 6 }}>
            <Typography.Text>Eigene Bilder</Typography.Text>{" "}
            <Typography.Text type="secondary">
              (optional, bis zu {FEEDBACK_MAX_ATTACHMENTS})
            </Typography.Text>
          </div>
          <Upload.Dragger
            accept="image/*"
            multiple
            showUploadList={false}
            disabled={isSubmitting || imageSlotsFull}
            // Nothing is uploaded here — the files are re-encoded and sent
            // with the feedback. beforeUpload runs per file with the whole
            // batch; handle the batch once.
            beforeUpload={(file, batch) => {
              if (file === batch[0]) void addImages(batch);
              return Upload.LIST_IGNORE;
            }}
          >
            <p className="ant-upload-drag-icon" style={{ marginBottom: 4 }}>
              <PictureOutlined style={{ fontSize: 28 }} />
            </p>
            <p className="ant-upload-text">Screenshot mit Strg+V einfügen oder hierher ziehen</p>
            <p className="ant-upload-hint">
              {imageSlotsFull
                ? `Maximal ${FEEDBACK_MAX_ATTACHMENTS} Bilder erreicht`
                : "oder klicken, um Bilder auszuwählen"}
            </p>
          </Upload.Dragger>

          {(images.length > 0 || pendingCount > 0) && (
            <>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
                {images.map((img, index) => (
                  <div
                    key={img.id}
                    style={{
                      position: "relative",
                      width: 96,
                      height: 72,
                      border: `1px solid ${token.colorBorder}`,
                      borderRadius: 6,
                      overflow: "hidden",
                      background: token.colorFillTertiary,
                    }}
                  >
                    <img
                      src={img.dataUrl}
                      alt={`Angehängtes Bild ${index + 1}`}
                      title={img.fileName}
                      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />
                    <Button
                      size="small"
                      shape="circle"
                      icon={<CloseOutlined />}
                      aria-label={`Bild ${index + 1} entfernen`}
                      title="Bild entfernen"
                      onClick={() => removeImage(img.id)}
                      style={{ position: "absolute", top: 4, right: 4 }}
                    />
                  </div>
                ))}
                {Array.from({ length: pendingCount }, (_, i) => (
                  <div
                    key={`pending-${i}`}
                    style={{
                      width: 96,
                      height: 72,
                      border: `1px dashed ${token.colorBorder}`,
                      borderRadius: 6,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Spin size="small" />
                  </div>
                ))}
              </div>
              <Typography.Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 4 }}>
                {images.length} von {FEEDBACK_MAX_ATTACHMENTS} Bildern
                {images.length > 0 ? ` · ${formatFileSize(totalImageBytes)}` : ""}
                {pendingCount > 0 ? " · wird vorbereitet …" : ""}
              </Typography.Text>
            </>
          )}
        </div>

        <Form.Item noStyle shouldUpdate={(prev, next) => prev.includeScreenshot !== next.includeScreenshot}>
          {({ getFieldValue }) => (
            <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 16 }}>
              {describeFeedbackContext(pageContext, !!screenshot && (getFieldValue("includeScreenshot") ?? true))}{" "}
              Dazu technische Informationen (Browser, Displaygröße, Browser-Konsolenprotokoll), damit sich der
              Fehler leichter nachvollziehen lässt.
            </Typography.Paragraph>
          )}
        </Form.Item>

      </Form>
    </Modal>
  );
}
